<script setup lang="ts">
import { FileText, Loader2, Search } from '@lucide/vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { CommandDialog, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command/index';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { useGlobalSearch } from '../composables/useGlobalSearch';
import type { SearchableModel } from '../types';

const { t, te } = useI18n();
const { isOpen, query, selectedModel, results, isLoading, error, selectResult, searchableModels } = useGlobalSearch();

const inputRef = ref<HTMLInputElement | null>(null);

// Auto-focus input when dialog opens
watch(isOpen, (open) => {
    if (open) {
        setTimeout(() => inputRef.value?.focus(), 0);
    }
});

/**
 * Translate a model label using its translationPrefix.
 */
function translateModelLabel(model: SearchableModel): string {
    if (model.translationPrefix) {
        const key = `models.${model.translationPrefix}.plural`;
        if (te(key)) {
            return t(key);
        }
    }
    return model.label;
}

/**
 * Searchable models sorted alphabetically by translated label.
 */
const sortedSearchableModels = computed(() => {
    return [...searchableModels.value].sort((a, b) => {
        const labelA = translateModelLabel(a).toLowerCase();
        const labelB = translateModelLabel(b).toLowerCase();
        return labelA.localeCompare(labelB);
    });
});

/**
 * Placeholder text based on current state.
 */
const placeholderText = computed(() => {
    return t('globalSearch.placeholder');
});

/**
 * Empty state message.
 */
const emptyMessage = computed(() => {
    if (error.value) {
        return t('globalSearch.error');
    }
    if (query.value && !isLoading.value && results.value?.total === 0) {
        return t('globalSearch.noResults');
    }
    return t('globalSearch.placeholder');
});

/**
 * Selected model label for display (translated).
 */
const selectedModelLabel = computed(() => {
    if (!selectedModel.value) {
        return t('globalSearch.allModels', 'All');
    }
    const model = searchableModels.value.find((m) => m.key === selectedModel.value);
    if (!model) {
        return selectedModel.value;
    }
    return translateModelLabel(model);
});

/**
 * Handle model filter change.
 */
function onModelChange(value: string): void {
    selectedModel.value = value === '__all__' ? null : value;
}
</script>

<template>
    <CommandDialog v-model:open="isOpen" :aria-label="t('globalSearch.dialogLabel', 'Global search')">
        <!-- Custom input with model filter to bypass internal filtering (server-side search) -->
        <div class="flex h-12 items-center gap-2 border-b pl-3 pr-12">
            <Search class="size-4 shrink-0 opacity-50" />
            <input
                ref="inputRef"
                v-model="query"
                type="text"
                :placeholder="placeholderText"
                :aria-label="t('globalSearch.inputLabel', 'Search all resources')"
                class="placeholder:text-muted-foreground flex h-full w-full rounded-md bg-transparent text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
            />
            <Select
                v-if="sortedSearchableModels.length > 1"
                :model-value="selectedModel ?? '__all__'"
                @update:model-value="onModelChange"
            >
                <SelectTrigger
                    size="sm"
                    class="h-8 w-auto min-w-[60px] max-w-[120px] shrink-0 border-none bg-transparent px-2 text-xs shadow-none focus-visible:ring-0"
                    :aria-label="t('globalSearch.modelFilter', 'Filter by model')"
                >
                    <SelectValue :placeholder="t('globalSearch.allModels', 'All')">
                        <span class="truncate">{{ selectedModelLabel }}</span>
                    </SelectValue>
                </SelectTrigger>
                <SelectContent align="end" class="!z-[9999]">
                    <SelectItem value="__all__">
                        {{ t('globalSearch.allModels', 'All') }}
                    </SelectItem>
                    <SelectItem v-for="model in sortedSearchableModels" :key="model.key" :value="model.key">
                        {{ translateModelLabel(model) }}
                    </SelectItem>
                </SelectContent>
            </Select>
        </div>
        <CommandList :aria-label="t('globalSearch.resultsLabel', 'Search results')">
            <!-- Loading State -->
            <div v-if="isLoading" class="text-muted-foreground flex items-center justify-center py-6 text-sm" role="status" aria-live="polite">
                <Loader2 class="mr-2 size-4 animate-spin" aria-hidden="true" />
                {{ t('globalSearch.searching') }}
            </div>

            <!-- Empty State -->
            <CommandEmpty v-else-if="!results || results.total === 0" role="status" aria-live="polite">
                {{ emptyMessage }}
            </CommandEmpty>

            <!-- Results -->
            <template v-else>
                <CommandGroup v-for="group in results.results" :key="group.model" :heading="group.title">
                    <CommandItem
                        v-for="item in group.items"
                        :key="item.id"
                        :value="`${group.model}-${item.id}`"
                        class="cursor-pointer"
                        @select="() => selectResult(item)"
                    >
                        <FileText class="text-muted-foreground mr-2 size-4 flex-shrink-0" aria-hidden="true" />
                        <div class="flex flex-1 flex-col overflow-hidden">
                            <span class="truncate">{{ item.label }}</span>
                            <span v-if="item.subtitle" class="text-muted-foreground truncate text-xs">
                                {{ item.subtitle }}
                            </span>
                        </div>
                    </CommandItem>
                </CommandGroup>
            </template>
        </CommandList>
    </CommandDialog>
</template>
